import React, { useMemo, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  CheckCircle2,
  Copy,
  FileText,
  History,
  Layers,
  MessageSquare,
  Paperclip,
  Send,
  ShieldCheck,
  XCircle,
} from 'lucide-react';
import { Alert } from '../../../shared/components/Alert';
import { Button } from '../../../shared/components/Button';
import { EmptyState } from '../../../shared/components/EmptyState';
import { Skeleton } from '../../../shared/components/Skeleton';
import type { PropostaVersao, StatusProposta } from '../api';
import { NegociacaoPropostaTimeline } from '../components/NegociacaoPropostaTimeline';
import {
  AceitePropostaModal,
  ConversaoEmContratoModal,
  EnvioPropostaModal,
  RejeicaoPropostaModal,
} from '../components/PropostaActionModals';
import { StatusBadge } from './PropostasComerciaisPage';
import {
  useAnexosDaProposta,
  useAprovacoesDaVersao,
  useContatosElegiveis,
  useDuplicarProposta,
  useItensDaVersao,
  useProposta,
  useSolicitarAprovacao,
} from '../hooks';
import { formatCurrency, formatValidade } from '../utils/format';
import { downloadAuthenticatedFile } from '../../../shared/lib/files';

type TabId =
  | 'identificacao'
  | 'itens'
  | 'aprovacoes'
  | 'envio'
  | 'negociacao'
  | 'aceite'
  | 'anexos'
  | 'versoes'
  | 'historico';

const TABS: Array<{ id: TabId; label: string; icon: React.ReactNode }> = [
  { id: 'identificacao', label: 'Identificação', icon: <FileText className="h-4 w-4" /> },
  { id: 'itens', label: 'Itens', icon: <Layers className="h-4 w-4" /> },
  { id: 'aprovacoes', label: 'Aprovações', icon: <ShieldCheck className="h-4 w-4" /> },
  { id: 'envio', label: 'Envio', icon: <Send className="h-4 w-4" /> },
  { id: 'negociacao', label: 'Negociação', icon: <MessageSquare className="h-4 w-4" /> },
  { id: 'aceite', label: 'Aceite', icon: <CheckCircle2 className="h-4 w-4" /> },
  { id: 'anexos', label: 'Anexos', icon: <Paperclip className="h-4 w-4" /> },
  { id: 'versoes', label: 'Versões', icon: <History className="h-4 w-4" /> },
  { id: 'historico', label: 'Histórico', icon: <History className="h-4 w-4" /> },
];

/**
 * Situações em que cada aba de ação faz sentido. Fora dessas, a aba é
 * desabilitada visualmente — evita cliques sem-efeito. Mesma lista da
 * state machine do backend (`FROZEN_STATUSES`, `EDITABLE_STATUSES`).
 */
const TAB_ENABLED_BY_STATUS: Partial<Record<TabId, StatusProposta[]>> = {
  envio: ['aprovada_internamente'],
  aceite: ['enviada_ao_cliente', 'em_negociacao'],
  negociacao: ['enviada_ao_cliente', 'em_negociacao'],
};

/**
 * Detalhe de uma proposta (US-COM-006, task 15.4).
 *
 * **Uma tela, 9 abas.** Cada aba consome hooks locais — o payload da
 * proposta chega uma vez via `useProposta` e é passado por props para
 * as sub-views. Modais de transição são acionados por botões dentro
 * das abas (Envio/Aceite/Rejeição/Conversão) e reusam o service do
 * backend.
 */
export default function PropostaDetalhePage(): React.ReactElement {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { data: proposta, isLoading, isError, error } = useProposta(id);
  const currentVersion = proposta?.versions?.find((v) => v.id === proposta.currentVersionId);

  const duplicar = useDuplicarProposta();
  const [tab, setTab] = useState<TabId>('identificacao');
  const [openEnvio, setOpenEnvio] = useState(false);
  const [openAceite, setOpenAceite] = useState(false);
  const [openRejeicao, setOpenRejeicao] = useState(false);
  const [openConversao, setOpenConversao] = useState(false);

  const isTabEnabled = (t: TabId): boolean => {
    if (!currentVersion) return t === 'identificacao';
    const restricts = TAB_ENABLED_BY_STATUS[t];
    if (!restricts) return true;
    return restricts.includes(currentVersion.status);
  };

  async function handleDuplicar() {
    if (!id) return;
    try {
      const nova = await duplicar.mutateAsync(id);
      navigate(`/propostas/${nova.id}`);
    } catch {
      // Erro exibe via toast global (hooks já invalidam); ignorar silêncio local
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-2 p-4 sm:p-6">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-10 w-full" />
        ))}
      </div>
    );
  }

  if (isError || !proposta) {
    return (
      <div className="p-4 sm:p-6">
        <Alert variant="error">
          Não foi possível carregar a proposta: {(error as Error)?.message ?? 'erro desconhecido'}
        </Alert>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6">
      <button
        type="button"
        onClick={() => navigate('/propostas')}
        className="mb-4 inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900"
      >
        <ArrowLeft className="h-4 w-4" />
        Voltar
      </button>

      <div className="mb-6 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-xl font-semibold text-gray-900">{proposta.codigo}</h1>
            {currentVersion && <StatusBadge status={currentVersion.status} />}
          </div>
          <p className="mt-1 text-sm text-gray-600">
            Versão vigente: {currentVersion?.versionNumber ?? '—'} · Valor:{' '}
            {formatCurrency(currentVersion?.valorTotal)} · Validade:{' '}
            {formatValidade(currentVersion?.dataValidade)}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={handleDuplicar} disabled={duplicar.isPending}>
            <Copy className="mr-2 h-4 w-4" />
            {duplicar.isPending ? 'Duplicando…' : 'Duplicar'}
          </Button>
        </div>
      </div>

      <nav aria-label="Abas" className="mb-4 flex flex-wrap gap-2 border-b border-gray-200">
        {TABS.map((t) => {
          const enabled = isTabEnabled(t.id);
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => enabled && setTab(t.id)}
              disabled={!enabled}
              className={`flex items-center gap-2 border-b-2 px-3 py-2 text-sm ${
                active
                  ? 'border-blue-600 text-blue-700'
                  : enabled
                    ? 'border-transparent text-gray-600 hover:text-gray-900'
                    : 'cursor-not-allowed border-transparent text-gray-300'
              }`}
            >
              {t.icon}
              {t.label}
            </button>
          );
        })}
      </nav>

      {tab === 'identificacao' && (
        <IdentificacaoTab
          proposta={proposta}
          currentVersion={currentVersion}
          onEnviar={() => setOpenEnvio(true)}
          onSolicitarAceite={() => setOpenAceite(true)}
          onRegistrarRejeicao={() => setOpenRejeicao(true)}
          onConverter={() => setOpenConversao(true)}
        />
      )}

      {tab === 'itens' && currentVersion && (
        <ItensTab proposalId={proposta.id} versionId={currentVersion.id} />
      )}

      {tab === 'aprovacoes' && currentVersion && (
        <AprovacoesTab proposalId={proposta.id} versionId={currentVersion.id} />
      )}

      {tab === 'envio' && currentVersion && (
        <EnvioTab
          proposalId={proposta.id}
          versionId={currentVersion.id}
          onAbrir={() => setOpenEnvio(true)}
          enabled={isTabEnabled('envio')}
        />
      )}

      {tab === 'negociacao' && currentVersion && (
        <NegociacaoPropostaTimeline
          proposalId={proposta.id}
          versionId={currentVersion.id}
          readonly={!isTabEnabled('negociacao')}
        />
      )}

      {tab === 'aceite' && currentVersion && (
        <AceiteTab
          proposalId={proposta.id}
          versionId={currentVersion.id}
          onAceite={() => setOpenAceite(true)}
          onRejeicao={() => setOpenRejeicao(true)}
          enabled={isTabEnabled('aceite')}
        />
      )}

      {tab === 'anexos' && currentVersion && (
        <AnexosTab proposalId={proposta.id} versionId={currentVersion.id} />
      )}

      {tab === 'versoes' && (
        <VersoesTab proposta={proposta} onOpenVersoes={() => navigate(`/propostas/${proposta.id}/versoes`)} />
      )}

      {tab === 'historico' && (
        <EmptyState
          title="Histórico"
          description="Trilha completa vive em proposal_change_history; UI dedicada virá em iteração posterior."
        />
      )}

      {currentVersion && (
        <>
          <EnvioPropostaModal
            open={openEnvio}
            onClose={() => setOpenEnvio(false)}
            proposalId={proposta.id}
            versionId={currentVersion.id}
          />
          <AceiteModalWrapper
            open={openAceite}
            onClose={() => setOpenAceite(false)}
            proposalId={proposta.id}
            versionId={currentVersion.id}
          />
          <RejeicaoPropostaModal
            open={openRejeicao}
            onClose={() => setOpenRejeicao(false)}
            proposalId={proposta.id}
            versionId={currentVersion.id}
          />
          <ConversaoEmContratoModal
            open={openConversao}
            onClose={() => setOpenConversao(false)}
            proposalId={proposta.id}
            versionId={currentVersion.id}
            valorTotal={currentVersion.valorTotal}
          />
        </>
      )}
    </div>
  );
}

function IdentificacaoTab({
  proposta,
  currentVersion,
  onEnviar,
  onSolicitarAceite,
  onRegistrarRejeicao,
  onConverter,
}: {
  proposta: NonNullable<ReturnType<typeof useProposta>['data']>;
  currentVersion: PropostaVersao | undefined;
  onEnviar: () => void;
  onSolicitarAceite: () => void;
  onRegistrarRejeicao: () => void;
  onConverter: () => void;
}) {
  const status = currentVersion?.status;
  const canEnviar = status === 'aprovada_internamente';
  const canAceite = status === 'enviada_ao_cliente' || status === 'em_negociacao';
  const canConverter = status === 'aceita';

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Field label="Cliente" value={proposta.clientId} mono />
        <Field label="Responsável comercial" value={proposta.responsavelComercialUserId} mono />
        <Field label="Origem" value={proposta.origem} />
        <Field label="Data de emissão" value={new Date(proposta.dataEmissao).toLocaleDateString('pt-BR')} />
        {currentVersion && (
          <>
            <Field label="Data de validade" value={formatValidade(currentVersion.dataValidade)} />
            <Field label="Valor total" value={formatCurrency(currentVersion.valorTotal)} />
            <Field
              label="Desconto"
              value={
                currentVersion.descontoPercentual
                  ? `${Number(currentVersion.descontoPercentual).toFixed(2)}% (${formatCurrency(currentVersion.descontoValorAbsoluto)})`
                  : '—'
              }
            />
            <Field
              label="Margem estimada"
              value={
                currentVersion.margemEstimadaPercentual
                  ? `${Number(currentVersion.margemEstimadaPercentual).toFixed(2)}%`
                  : 'sem custo declarado'
              }
            />
          </>
        )}
        {proposta.sourceOpportunityId && (
          <Field label="Oportunidade origem" value={proposta.sourceOpportunityId} mono />
        )}
      </div>

      {currentVersion?.escopo && (
        <Section title="Escopo">
          <p className="whitespace-pre-line text-sm text-gray-700">{currentVersion.escopo}</p>
        </Section>
      )}

      {currentVersion?.observacoes && (
        <Section title="Observações">
          <p className="whitespace-pre-line text-sm text-gray-700">{currentVersion.observacoes}</p>
        </Section>
      )}

      <div className="flex flex-wrap gap-2 border-t border-gray-200 pt-4">
        <Button onClick={onEnviar} disabled={!canEnviar}>
          <Send className="mr-2 h-4 w-4" />
          Enviar ao cliente
        </Button>
        <Button variant="secondary" onClick={onSolicitarAceite} disabled={!canAceite}>
          <CheckCircle2 className="mr-2 h-4 w-4" />
          Registrar aceite
        </Button>
        <Button variant="secondary" onClick={onRegistrarRejeicao} disabled={!canAceite}>
          <XCircle className="mr-2 h-4 w-4" />
          Registrar rejeição
        </Button>
        <Button variant="secondary" onClick={onConverter} disabled={!canConverter}>
          <FileText className="mr-2 h-4 w-4" />
          Converter em contrato
        </Button>
      </div>
    </div>
  );
}

function ItensTab({ proposalId, versionId }: { proposalId: string; versionId: string }) {
  const { data, isLoading } = useItensDaVersao(proposalId, versionId);
  if (isLoading) return <Skeleton className="h-20 w-full" />;
  const itens = data ?? [];
  if (itens.length === 0)
    return (
      <EmptyState
        title="Sem itens"
        description="Adicione itens do catálogo ou popule a partir da Base Comercial."
      />
    );
  return (
    <div className="-mx-4 overflow-x-auto sm:mx-0">
      <table className="min-w-full divide-y divide-gray-200 text-sm">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-4 py-2 text-left font-medium text-gray-700">Serviço</th>
            <th className="px-4 py-2 text-left font-medium text-gray-700">Perfil</th>
            <th className="px-4 py-2 text-right font-medium text-gray-700">Qtd.</th>
            <th className="px-4 py-2 text-right font-medium text-gray-700">V. unit.</th>
            <th className="px-4 py-2 text-right font-medium text-gray-700">Total item</th>
            <th className="px-4 py-2 text-left font-medium text-gray-700">Override</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 bg-white">
          {itens.map((it) => (
            <tr key={it.id}>
              <td className="px-4 py-2 text-gray-900">{it.servicoSlug}</td>
              <td className="px-4 py-2 text-gray-700">{it.perfilProfissionalSlug ?? '—'}</td>
              <td className="px-4 py-2 text-right text-gray-700">{it.quantidade}</td>
              <td className="px-4 py-2 text-right text-gray-700">{formatCurrency(it.valorUnitario)}</td>
              <td className="px-4 py-2 text-right text-gray-900">{formatCurrency(it.valorTotalItem)}</td>
              <td className="px-4 py-2 text-gray-700">
                {it.isOverride ? (
                  <span className="rounded bg-amber-100 px-1.5 py-0.5 text-xs text-amber-800">
                    override
                  </span>
                ) : (
                  '—'
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AprovacoesTab({ proposalId, versionId }: { proposalId: string; versionId: string }) {
  const { data, isLoading } = useAprovacoesDaVersao(versionId);
  const solicitar = useSolicitarAprovacao(proposalId, versionId);
  if (isLoading) return <Skeleton className="h-20 w-full" />;
  const linhas = data ?? [];
  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <Button
          variant="secondary"
          onClick={() => solicitar.mutate(undefined)}
          disabled={solicitar.isPending}
        >
          {solicitar.isPending ? 'Solicitando…' : 'Solicitar aprovação'}
        </Button>
      </div>
      {linhas.length === 0 ? (
        <EmptyState
          title="Sem aprovações"
          description="Ao solicitar aprovação, o workflow parametrizável cria os passos e notifica os aprovadores."
        />
      ) : (
        <ol className="space-y-2">
          {linhas.map((a) => (
            <li
              key={a.id}
              className="flex items-center justify-between rounded border border-gray-200 bg-white p-3 text-sm"
            >
              <div>
                <div className="font-medium text-gray-900">
                  Etapa {a.stepIndex + 1} · {a.roleSlug}
                </div>
                <div className="text-xs text-gray-500">Feature: {a.featureRequired}</div>
              </div>
              <span
                className={`rounded-full px-2 py-0.5 text-xs ${
                  a.status === 'aprovada'
                    ? 'bg-emerald-100 text-emerald-800'
                    : a.status === 'rejeitada'
                      ? 'bg-red-100 text-red-800'
                      : a.status === 'devolvida'
                        ? 'bg-yellow-100 text-yellow-800'
                        : 'bg-blue-100 text-blue-800'
                }`}
              >
                {a.status}
              </span>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

function EnvioTab({
  proposalId,
  versionId,
  onAbrir,
  enabled,
}: {
  proposalId: string;
  versionId: string;
  onAbrir: () => void;
  enabled: boolean;
}) {
  return (
    <div className="space-y-3">
      {!enabled && (
        <Alert variant="info">
          Envio disponível apenas em "Aprovada Internamente" (RN-COM-017).
        </Alert>
      )}
      <Button onClick={onAbrir} disabled={!enabled}>
        <Send className="mr-2 h-4 w-4" />
        Enviar proposta
      </Button>
      <p className="text-xs text-gray-500">
        O PDF é gerado no cliente e o SHA-256 é registrado como evidência do envio (§D8).
      </p>
    </div>
  );
}

function AceiteTab({
  proposalId,
  versionId,
  onAceite,
  onRejeicao,
  enabled,
}: {
  proposalId: string;
  versionId: string;
  onAceite: () => void;
  onRejeicao: () => void;
  enabled: boolean;
}) {
  return (
    <div className="space-y-3">
      {!enabled && (
        <Alert variant="info">
          Aceite/rejeição só é registrável após envio ao cliente (RN-COM-021/023).
        </Alert>
      )}
      <div className="flex flex-wrap gap-2">
        <Button onClick={onAceite} disabled={!enabled}>
          <CheckCircle2 className="mr-2 h-4 w-4" />
          Registrar aceite
        </Button>
        <Button variant="secondary" onClick={onRejeicao} disabled={!enabled}>
          <XCircle className="mr-2 h-4 w-4" />
          Registrar rejeição
        </Button>
      </div>
    </div>
  );
}

function AnexosTab({ proposalId, versionId }: { proposalId: string; versionId: string }) {
  const { data, isLoading } = useAnexosDaProposta(proposalId, versionId);

  const handleDownload = useCallback(
    async (attachmentId: string, filename: string) => {
      await downloadAuthenticatedFile(
        `/api/proposals/${proposalId}/attachments/${attachmentId}/download`,
        filename,
      );
    },
    [proposalId],
  );

  if (isLoading) return <Skeleton className="h-20 w-full" />;
  const anexos = data ?? [];
  if (anexos.length === 0)
    return (
      <EmptyState
        title="Sem anexos"
        description="Anexos ficam presos à versão em que foram enviados e são herdados nas versões seguintes."
      />
    );
  return (
    <ul className="space-y-2">
      {anexos.map((a) => (
        <li
          key={a.id}
          className="flex items-center justify-between rounded border border-gray-200 bg-white p-3 text-sm"
        >
          <div className="min-w-0">
            <p className="truncate font-medium text-gray-900">{a.filename}</p>
            <p className="text-xs text-gray-500">
              {a.mimeType} · {(Number(a.sizeBytes) / 1024).toFixed(1)} KB
            </p>
          </div>
          <button
            type="button"
            onClick={() => handleDownload(a.id, a.filename)}
            className="text-xs text-blue-700 hover:underline"
          >
            Baixar
          </button>
        </li>
      ))}
    </ul>
  );
}

function VersoesTab({
  proposta,
  onOpenVersoes,
}: {
  proposta: NonNullable<ReturnType<typeof useProposta>['data']>;
  onOpenVersoes: () => void;
}) {
  const versoes = proposta.versions ?? [];
  return (
    <div className="space-y-3">
      <Button variant="secondary" onClick={onOpenVersoes}>
        Comparar versões
      </Button>
      <ul className="space-y-2">
        {versoes.map((v) => (
          <li
            key={v.id}
            className="flex items-center justify-between rounded border border-gray-200 bg-white p-3 text-sm"
          >
            <div>
              <div className="font-medium text-gray-900">v{v.versionNumber}</div>
              <div className="text-xs text-gray-500">
                Valor: {formatCurrency(v.valorTotal)} · Criada em{' '}
                {new Date(v.createdAt).toLocaleDateString('pt-BR')}
              </div>
            </div>
            <StatusBadge status={v.status} />
          </li>
        ))}
      </ul>
    </div>
  );
}

function AceiteModalWrapper({
  open,
  onClose,
  proposalId,
  versionId,
}: {
  open: boolean;
  onClose: () => void;
  proposalId: string;
  versionId: string;
}) {
  const { data: contatos } = useContatosElegiveis(proposalId);
  const { data: anexos } = useAnexosDaProposta(proposalId, versionId);
  const contactList = useMemo(() => contatos ?? [], [contatos]);
  const attachmentList = useMemo(() => anexos ?? [], [anexos]);
  return (
    <AceitePropostaModal
      open={open}
      onClose={onClose}
      proposalId={proposalId}
      versionId={versionId}
      clientContacts={contactList}
      attachments={attachmentList}
    />
  );
}

function Field({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="rounded-md bg-gray-50 px-3 py-2">
      <span className="text-xs uppercase text-gray-500">{label}</span>
      <p className={`mt-0.5 text-sm text-gray-900 ${mono ? 'font-mono text-xs' : ''}`}>{value}</p>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="rounded-md border border-gray-200 bg-white p-4">
      <h3 className="mb-2 text-sm font-medium text-gray-900">{title}</h3>
      {children}
    </section>
  );
}

export { PropostaDetalhePage };
